/**
 * FollowUpProbeCard
 *
 * Renders one Phase 3 follow-up probe with dual-audience framing.
 * Unlike QuestionDeepDive (primary questions), probes are NOT gated —
 * strong/weak signals are calibration material for both audiences.
 *
 * CANDIDATE FRAMING
 * ─────────────
 *   "If your primary answer was strong, expect this follow-up."
 *   - trigger = "when this appears in your answer, you'll get this probe"
 *   - strongSignal = "what you should aim for in your follow-up response"
 *   - weakSignal = "common shallow responses to avoid"
 *   - escalationProbe = "if you nail the follow-up, expect this too"
 *
 * HIRING MANAGER FRAMING
 * ─────────────
 *   "Deploy this probe when the candidate's primary answer [trigger]."
 *   - strongSignal = "score 4-5 if the candidate says this"
 *   - weakSignal = "score 2-3 if the candidate says this"
 *   - escalationProbe = "deploy this further probe to distinguish 4 from 5"
 *
 * Same source content; framing switches per audience toggle.
 */

import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useTranslation } from "react-i18next";
import type { Phase3Probe } from "../data/phase3Probes";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  probe: Phase3Probe;
  audience: "candidate" | "hiringManager";
  levelColor: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FollowUpProbeCard({ probe, audience, levelColor }: Props) {
  const theme = useTheme();
  const { t } = useTranslation("common");

  // Audience-specific framing labels
  const labels = {
    trigger: t(
      audience === "candidate"
        ? "FollowUpProbeCard.candidateTrigger"
        : "FollowUpProbeCard.hmTrigger"
    ),
    question: t(
      audience === "candidate"
        ? "FollowUpProbeCard.candidateQuestion"
        : "FollowUpProbeCard.hmQuestion"
    ),
    strong: t(
      audience === "candidate"
        ? "FollowUpProbeCard.candidateStrong"
        : "FollowUpProbeCard.hmStrong"
    ),
    weak: t(
      audience === "candidate"
        ? "FollowUpProbeCard.candidateWeak"
        : "FollowUpProbeCard.hmWeak"
    ),
    escalation: t(
      audience === "candidate"
        ? "FollowUpProbeCard.candidateEscalation"
        : "FollowUpProbeCard.hmEscalation"
    ),
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${alpha(levelColor, 0.5)}`,
        borderRadius: 2,
        p: 2.5,
        transition: "border-color 0.2s",
        "&:hover": {
          borderColor: alpha(levelColor, 0.3),
          borderLeftColor: levelColor,
        },
      }}
    >
      {/* ── Header: slug + probe indicator ─────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" gap={0.5}>
        <QuestionAnswerOutlinedIcon sx={{ fontSize: 16, color: levelColor }} />
        <Chip
          label={probe.slug.toUpperCase()}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: 500,
            fontSize: "0.68rem",
            bgcolor: alpha(levelColor, 0.1),
            color: levelColor,
            height: 20,
          }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600 }}>
          {t("FollowUpProbeCard.followUpProbe")}
        </Typography>
      </Stack>

      {/* ── Trigger ────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.25} alignItems="flex-start" mb={2}>
        <FlagOutlinedIcon sx={{ color: theme.palette.text.secondary, fontSize: 16, mt: "2px", flexShrink: 0 }} />
        <Box>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.text.secondary, letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            {labels.trigger}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55, mt: 0.25, fontStyle: "italic" }}>
            {probe.trigger}
          </Typography>
        </Box>
      </Stack>

      {/* ── Question text ──────────────────────────────────────────────── */}
      <Box mb={2} pl={3.25}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: levelColor, letterSpacing: "0.04em", textTransform: "uppercase" }}
        >
          {labels.question}
        </Typography>
        <Typography
          variant="body1"
          fontWeight={500}
          sx={{ lineHeight: 1.55, mt: 0.5, color: "text.primary" }}
        >
          {probe.question}
        </Typography>
      </Box>

      {/* ── Strong signal ──────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.success.main, 0.05),
          border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
          borderRadius: 2,
          p: 1.75,
          mb: 1.25,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <TrendingUpIcon sx={{ color: theme.palette.success.dark, fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.success.dark, letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {labels.strong}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5 }}>
              {probe.strongSignal}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── Weak signal ────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.error.main, 0.04),
          border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
          borderRadius: 2,
          p: 1.75,
          mb: probe.escalationProbe ? 1.25 : 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <TrendingDownIcon sx={{ color: theme.palette.error.dark, fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.error.dark, letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {labels.weak}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5 }}>
              {probe.weakSignal}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── Escalation probe (optional) ─────────────────────────────────── */}
      {probe.escalationProbe && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
            borderRadius: 2,
            p: 1.75,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <ArrowUpwardIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, letterSpacing: "0.04em", textTransform: "uppercase" }}
              >
                {labels.escalation}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5, fontWeight: 500 }}>
                {probe.escalationProbe}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}
    </Paper>
  );
}
